/**
 * Phase 45 Plan 45-08 (D-15) — TaskDetailSlideOver acceptance criteria section.
 *
 * Renders the Task.acceptanceCriteria JSONB array as a checklist with:
 *   - inline edit (double-click text)
 *   - add new (input + Enter)
 *   - delete (trash icon + confirm)
 *   - toggle done (checkbox) — single-item PATCH per D-15 (avoids full-task race)
 *
 * Optimistic UI lives in useTaskAcceptance; this component is presentation.
 */
import { useState, type KeyboardEvent } from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  useTaskAcceptance,
  type AcceptanceItem,
  type AcceptanceAudience,
} from '../../hooks/useTaskAcceptance';
import { MarkdownBody } from '../comments/MarkdownBody';

const ACCEPTANCE_TEXT_MAX = 500;

interface AcceptanceCriteriaListProps {
  taskId: number;
  /**
   * External acceptance v1 (260702) — `external` routes reads + mutations to
   * the external transport (/tasks/:id/external-acceptance, cookie creds). This
   * fixes the 401 the external portal hit when the section mounted ungated.
   * `internal` (default) is unchanged.
   */
  audience?: AcceptanceAudience;
}

export function AcceptanceCriteriaList({
  taskId,
  audience = 'internal',
}: AcceptanceCriteriaListProps) {
  const { data, isLoading, addItem, toggleItem, updateText, deleteItem } =
    useTaskAcceptance(taskId, audience);
  const [draft, setDraft] = useState('');

  const total = data.length;
  const done = data.filter((it) => it.done).length;

  function submitDraft() {
    const text = draft.trim();
    if (text.length === 0) return;
    addItem.mutate(text);
    setDraft('');
  }

  function handleDraftKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitDraft();
    }
  }

  return (
    <section
      aria-label="Acceptance criteria"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="acceptance-criteria-section"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
          Acceptance criteria
        </h3>
        {total > 0 && (
          <span className="text-xs text-ms-text-secondary tabular-nums">
            {done} of {total}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-ms-text-muted italic">Loading criteria…</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No acceptance criteria yet.</p>
      ) : (
        <ul className="space-y-1" data-testid="acceptance-list">
          {data.map((item) => (
            <AcceptanceRow
              key={item.id}
              item={item}
              onToggle={(done) =>
                toggleItem.mutate({ itemId: item.id, done })
              }
              onUpdateText={(text) =>
                updateText.mutate({ itemId: item.id, text })
              }
              onDelete={() => deleteItem.mutate(item.id)}
            />
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleDraftKey}
          placeholder="Add an acceptance criterion…"
          maxLength={ACCEPTANCE_TEXT_MAX}
          aria-label="New acceptance criterion"
          data-testid="acceptance-new-input"
          className="flex-1 text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
        <button
          type="button"
          onClick={submitDraft}
          disabled={draft.trim().length === 0 || addItem.isPending}
          aria-label="Add acceptance criterion"
          data-testid="acceptance-add-btn"
          className="inline-flex items-center gap-1 rounded-lg border border-ms-border bg-ms-surface px-2 py-1 text-xs font-medium text-ms-text hover:bg-ms-canvas disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </button>
      </div>
    </section>
  );
}

interface AcceptanceRowProps {
  item: AcceptanceItem;
  onToggle: (done: boolean) => void;
  onUpdateText: (text: string) => void;
  onDelete: () => void;
}

function AcceptanceRow({
  item,
  onToggle,
  onUpdateText,
  onDelete,
}: AcceptanceRowProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.text);

  function commit() {
    const next = editValue.trim();
    if (next.length > 0 && next !== item.text) {
      onUpdateText(next);
    } else {
      setEditValue(item.text);
    }
    setEditing(false);
  }

  function handleEditKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditValue(item.text);
      setEditing(false);
    }
  }

  function handleDelete() {
    const ok = window.confirm('Delete this acceptance criterion?');
    if (ok) onDelete();
  }

  return (
    <li
      className="flex items-start gap-2 group"
      data-testid={`acceptance-row-${item.id}`}
    >
      <input
        type="checkbox"
        checked={item.done}
        onChange={(e) => onToggle(e.target.checked)}
        aria-label={item.done ? 'Mark not done' : 'Mark done'}
        data-testid={`acceptance-toggle-${item.id}`}
        className="mt-1 h-4 w-4 rounded border-ms-border text-primary-500 focus:ring-primary-400"
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commit}
            onKeyDown={handleEditKey}
            autoFocus
            maxLength={ACCEPTANCE_TEXT_MAX}
            aria-label="Edit acceptance criterion"
            data-testid={`acceptance-edit-${item.id}`}
            className="w-full text-sm font-medium text-ms-text px-2 py-1 border border-ms-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        ) : (
          <div
            onDoubleClick={() => setEditing(true)}
            title="Double-click to edit"
            className={`text-sm text-left w-full break-words cursor-text ${
              item.done ? 'line-through text-ms-text-muted' : 'text-ms-text'
            }`}
            data-testid={`acceptance-text-${item.id}`}
          >
            <MarkdownBody body={item.text} className="text-sm" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleDelete}
        aria-label="Delete acceptance criterion"
        data-testid={`acceptance-delete-${item.id}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 flex items-center justify-center text-ms-text-muted hover:text-rose-600 rounded-md hover:bg-rose-50"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </li>
  );
}
